import './CreateEditDialog.scss';

import { Form, Formik } from 'formik';
import _ from 'lodash';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { TabPanel, TabView } from 'primereact/tabview';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import useAxios from '../../../../hooks/useAxios';
import { invalidTabHeaderIcon } from '../../../../utils/globals';
import { formik_hasDialogTabErrors } from '../../../../utils/helpers';
import { tryApiContextValidation } from '../../../../utils/helpers/formik';
import { yupMaxField, yupRequiredField } from '../../../../utils/helpers/yup';
import {
  basicConfigTabFields,
  getAllowedErrors,
  getFieldMappingTabFields,
  getInitialValues,
  toApiData,
} from './CreateEditDialog.functions';
import CreateEditDialogBasicConfigTab from './CreateEditDialogBasicConfigTab';
import CreateEditDialogFieldMappingTab from './CreateEditDialogFieldMappingTab';

function CreateEditDialog({
  data = {},
  isShown,
  onHide,
  onCreateEditSubmit,
  onListFieldSubmit,
  onFieldDefinitionSubmit,
}) {
  const { t } = useTranslation();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [deletedFields, setDeletedFields] = useState([]);

  const formRef = useRef(null);

  const isEditDialog = Object.keys(data).length > 0;

  const {
    reload: formSubmissionRequest,
    isLoading: isFormSubmissionRequestLoading,
  } = useAxios();

  const { data: entities, isLoading: isEntitiesLoading } =
    useAxios('/entities');

  const { data: entityProperties, isLoading: isEntityPropertiesLoading } =
    useAxios('/entityproperties', undefined, { method: 'GET' });

  const { data: pricebooks, isLoading: isPricebooksLoading } = useAxios(
    '/price-lists?limit=0'
  );

  const initialValues = useMemo(
    () => getInitialValues(data, entities, entityProperties),
    [data, entities, entityProperties]
  );

  useEffect(() => {
    setActiveTabIndex(0);
    setDeletedFields([]);
  }, [isShown]);

  function handleFieldDelete(field) {
    setDeletedFields((prevState) => [...prevState, field]);
  }

  function sendFieldRequest(params) {
    return new Promise((resolve) => {
      formSubmissionRequest({
        ...params,
        successCallback: () => resolve(true),
        errorCallback: () => resolve(false),
      });
    });
  }

  async function handleFieldsRequest(values, listId) {
    // Deleted fields first, then properties moved between saved fields are
    // released from their old field: the API refuses a property another
    // field of the list still holds (existing_property_id).
    const filteredDeletedFields = deletedFields.filter(
      (f) => !String(f.id).match('_new_')
    );

    await Promise.all(
      filteredDeletedFields.map(async (field) => {
        const isDeleted = await sendFieldRequest({
          url: `/importlisttypefields/${field.id}`,
          method: 'DELETE',
        });

        handleFieldDelete(field.name, isDeleted);
      })
    );

    const savedPropertyIds = new Map(
      (data.fields ?? []).map((f) => [f.id, f.property_id])
    );

    await Promise.all(
      values.fields
        .filter(
          (f) =>
            savedPropertyIds.get(f.id) &&
            savedPropertyIds.get(f.id) !== (f.property_id || null)
        )
        .map((f) =>
          sendFieldRequest({
            url: `/importlisttypefields/${f.id}`,
            method: 'PUT',
            // 0 clears the property, the API skips a null one
            payload: { property_id: 0 },
          })
        )
    );

    // New and existing fields
    for (let field of values.fields) {
      const isNewField = !!String(field.id).match('_new_');

      let payload = {
        ...field,
        import_list_type_id: listId,
        print: '1',
      };

      payload.property_id = payload.property_id || 0;

      delete payload.id;
      delete payload.entity_id;
      delete payload.entityname;
      delete payload.propertyname;

      sendFieldRequest({
        url: isNewField
          ? '/importlisttypefields'
          : `/importlisttypefields/${field.id}`,
        payload: payload,
        method: isNewField ? 'POST' : 'PUT',
      }).then((isSaved) => onListFieldSubmit(field.name, isNewField, isSaved));
    }
  }

  function handleFieldDefinitions(values, listId) {
    const fields = values.fields.map((f) => ({
      name: f.name,
      property_id: f.property_id,
    }));

    formSubmissionRequest({
      url: '/importlistparceldata/definition',
      method: 'POST',
      payload: {
        import_list_type_id: listId,
        fields,
      },
      successCallback: () => {
        onFieldDefinitionSubmit(values.name, true);
      },
      errorCallback: () => {
        onFieldDefinitionSubmit(values.name, false);
      },
    });
  }

  function handleCreateRequest(values) {
    formSubmissionRequest({
      url: '/importlisttypes',
      payload: toApiData(values, isEditDialog),
      method: 'POST',
      successCallback: (data) => {
        onCreateEditSubmit(values.name, true, true);
        handleFieldsRequest(values, data.id);
        handleFieldDefinitions(values, data.id);
      },
      errorCallback: (err) => {
        const allowedErrors = getAllowedErrors(t);
        const errorDescription = err?.response?.data.error_description;

        if (
          errorDescription &&
          tryApiContextValidation(errorDescription, formRef, allowedErrors)
        ) {
          return;
        }
        onCreateEditSubmit(values.name, true, false);
      },
    });
  }

  function handleEditRequest(values) {
    formSubmissionRequest({
      url: `/importlisttypes/${data.id}`,
      // Yes, id is required both as a query and a body parameter...
      payload: toApiData({ ...values, id: data.id }, isEditDialog),
      method: 'PUT',
      successCallback: () => {
        onCreateEditSubmit(values.name, false, true);
      },
      errorCallback: (err) => {
        const allowedErrors = getAllowedErrors(t);
        const errorDescription = err?.response?.data.error_description;

        if (
          errorDescription &&
          tryApiContextValidation(errorDescription, formRef, allowedErrors)
        ) {
          return;
        }
        onCreateEditSubmit(values.name, false, false);
      },
    });

    if (!_.isEqual(data.fields, values.fields)) {
      handleFieldsRequest(values, data.id);
      handleFieldDefinitions(values, data.id);
    }
  }

  function handleFormSubmision(values) {
    if (isEditDialog) {
      handleEditRequest(values);
    } else {
      handleCreateRequest(values);
    }
  }

  const dialogHeader = isEditDialog
    ? t('Editing - {{dataName}}', { dataName: data.name })
    : t('Add new import list type');
  const dialogFooter = (
    <>
      <Button
        type="button"
        label={t('Cancel')}
        className="p-button-text"
        onClick={() => onHide()}
      />

      <Button
        type="button"
        label={
          isFormSubmissionRequestLoading
            ? t('Saving...')
            : isEditDialog
            ? t('Save changes')
            : t('Add list type')
        }
        disabled={isFormSubmissionRequestLoading}
        onClick={() => {
          if (formRef.current) {
            formRef.current.handleSubmit();
          }
        }}
        data-cy="submit"
      />
    </>
  );

  const validationSchema = useMemo(() => {
    const pricebookId = Yup.string()
      .required()
      .test({
        message: `${t('Price list')}: ${t('Does not exist')}`,
        test: (value) =>
          !pricebooks ||
          Number(value) === 0 ||
          pricebooks.data.some((p) => p.id === Number(value)),
      });

    return Yup.object().shape({
      name: Yup.string()
        .required()
        .test({
          message: yupRequiredField(t, t('Name')),
          test: (value) => value !== '0',
        })
        .max(100, ({ max }) => yupMaxField(t, t('Name'), max)),
      klient_isSender: Yup.string().required(),
      plaka_postarina_isprakac: Yup.string().required(),
      plaka_otkup_isprakac: Yup.string().required(),
      plaka_povraten_dokument_isprakac: Yup.string().required(),
      plaka_osiguruvanje_isprakac: Yup.string().required(),
      isprakac_cenovnik_id: pricebookId,
      primac_cenovnik_id: pricebookId,
      naracatel_cenovnik_id: pricebookId,
      fields: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string(),
            entityname: Yup.string().nullable(),
            propertyname: Yup.string().nullable(),
            isRequired: Yup.string(),
            default_value: Yup.string(),
          })
        )
        .required()
        .min(1, yupRequiredField(t, t('Field mapping')))
        .test({
          test: function (value) {
            for (const field of value ?? []) {
              const name = field.name ?? '';

              if (name === '' || name === '0') {
                return this.createError({
                  message: yupRequiredField(t, t('Field name')),
                });
              }

              if (name.length > 100) {
                return this.createError({
                  message: yupMaxField(t, t('Field name'), 100),
                });
              }

              if ((field.default_value ?? '').length > 45) {
                return this.createError({
                  message: yupMaxField(t, t('Default value'), 45),
                });
              }
            }

            const mapped = (value ?? []).filter(
              (f) => !_.isNil(f.property_id) && f.property_id !== ''
            );
            const duplicateProperties = _.uniq(
              mapped
                .filter(
                  (f, i) =>
                    mapped.findIndex(
                      (other) => other.property_id === f.property_id
                    ) !== i
                )
                .map((f) => f.propertyname ?? f.property_id)
            );

            if (duplicateProperties.length > 0) {
              return this.createError({
                message: t(
                  'The following properties are mapped to more than one field: {{duplicateProperties}}',
                  {
                    duplicateProperties: duplicateProperties.join(', '),
                    nsSeparator: false,
                  }
                ),
              });
            }

            return true;
          },
        }),
    });
  }, [t, pricebooks]);

  return (
    <Dialog
      visible={isShown}
      onHide={onHide}
      header={dialogHeader}
      footer={dialogFooter}
      maximizable
      maximized
      resizable={false}
      className="list-types-create-edit-dialog"
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleFormSubmision}
      >
        {({ values, errors, touched }) => {
          const fieldMappingTabFields = getFieldMappingTabFields(
            values.klient_isSender
          );

          const basicConfigTabIcon = formik_hasDialogTabErrors(
            errors,
            basicConfigTabFields
          )
            ? invalidTabHeaderIcon
            : {};

          const fieldMappingTabIcon = formik_hasDialogTabErrors(
            errors,
            fieldMappingTabFields
          )
            ? invalidTabHeaderIcon
            : {};

          return (
            <Form>
              <TabView
                activeIndex={activeTabIndex}
                onTabChange={(e) => setActiveTabIndex(e.index)}
                className={
                  Object.keys(errors).length > 0 &&
                  Object.keys(touched).length > 0
                    ? 'invalid'
                    : ''
                }
              >
                <TabPanel
                  header={t('Basic configuration')}
                  {...basicConfigTabIcon}
                >
                  <CreateEditDialogBasicConfigTab
                    entities={entities}
                    entityProperties={entityProperties}
                    pricebooks={pricebooks}
                    isPricebooksLoading={isPricebooksLoading}
                  />
                </TabPanel>

                <TabPanel
                  header={t('Field mapping')}
                  headerClassName="data-cy-field-mapping-tab"
                  {...fieldMappingTabIcon}
                >
                  <CreateEditDialogFieldMappingTab
                    onFieldDelete={handleFieldDelete}
                    entities={entities}
                    isEntitiesLoading={isEntitiesLoading}
                    entityProperties={entityProperties}
                    isEntityPropertiesLoading={isEntityPropertiesLoading}
                  />
                </TabPanel>
              </TabView>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}

export default CreateEditDialog;
